import React from 'react';
import { Filter, X, Check } from 'lucide-react';
import { DIFFICULTY_LEVELS, QUESTION_TYPES, difficultyConfig, questionTypeConfig } from '../data/questionTypes';

const AdvancedFilters = ({ 
  isOpen, 
  onClose, 
  selectedDifficulties, 
  setSelectedDifficulties,
  selectedTypes,
  setSelectedTypes 
}) => {
  if (!isOpen) return null;

  const toggleDifficulty = (difficulty) => {
    setSelectedDifficulties(prev => {
      if (prev.includes(difficulty)) {
        return prev.filter(d => d !== difficulty);
      } else {
        return [...prev, difficulty];
      }
    });
  };

  const toggleType = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const selectAllDifficulties = () => {
    setSelectedDifficulties(Object.values(DIFFICULTY_LEVELS));
  };

  const selectAllTypes = () => {
    setSelectedTypes(Object.values(QUESTION_TYPES));
  };

  const getDifficultyColor = (difficulty) => {
    const config = difficultyConfig[difficulty];
    const colors = {
      green: 'from-green-100 to-emerald-100 border-green-300 text-green-800',
      blue: 'from-blue-100 to-cyan-100 border-blue-300 text-blue-800',
      red: 'from-red-100 to-rose-100 border-red-300 text-red-800'
    };
    return colors[config.color] || 'from-gray-100 to-gray-200 border-gray-300 text-gray-800';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl">
                <Filter className="w-8 h-8 text-purple-500" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Filtros Avanzados</h2>
                <p className="text-sm text-gray-600">Personaliza tu experiencia de aprendizaje</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Difficulty Section */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-700">Nivel de Dificultad</h3>
              <button
                onClick={selectAllDifficulties}
                className="px-4 py-2 text-sm bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg font-semibold transition-colors"
              >
                Seleccionar Todos
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(difficultyConfig).map(([key, config]) => {
                const isSelected = selectedDifficulties.includes(key);
                return (
                  <button
                    key={key}
                    onClick={() => toggleDifficulty(key)}
                    className={`p-5 rounded-2xl border-2 transition-all transform hover:scale-105 active:scale-95 ${
                      isSelected
                        ? `bg-gradient-to-br ${getDifficultyColor(key)} shadow-md`
                        : 'bg-white border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{config.icon}</span>
                        <h4 className="font-bold text-lg">{config.label}</h4>
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-current" />}
                    </div>
                    <p className="text-sm text-left">{config.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Question Types Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-700">Tipo de Pregunta</h3>
              <button
                onClick={selectAllTypes}
                className="px-4 py-2 text-sm bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg font-semibold transition-colors"
              >
                Seleccionar Todos
              </button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(questionTypeConfig).map(([key, config]) => {
                const isSelected = selectedTypes.includes(key);
                return (
                  <button
                    key={key}
                    onClick={() => toggleType(key)}
                    className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 active:scale-95 ${
                      isSelected
                        ? 'bg-gradient-to-br from-blue-100 to-cyan-100 border-blue-300 text-blue-800 shadow-md'
                        : 'bg-white border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl">{config.icon}</span>
                      <span className="font-bold text-xs text-center">{config.label}</span>
                      {isSelected && <Check className="w-4 h-4 text-current" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilters;
